import React, { useState } from 'react';

/**
 * SupportTicketModal — IMP 11.
 *
 * Lets the agent file a support ticket. Subject, Description, Category,
 * Priority, and an optional attachment. Submits to
 * `POST /api/support/tickets` which both persists to MongoDB and emails
 * support@estatel.ai with the attachment inlined.
 */
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CATEGORIES = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export default function SupportTicketModal({ open, onClose }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('question');
  const [priority, setPriority] = useState('med');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  if (!open) return null;

  const reset = () => {
    setSubject(''); setDescription(''); setCategory('question');
    setPriority('med'); setFile(null); setError(''); setSuccess(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose && onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('subject', subject);
      fd.append('description', description);
      fd.append('category', category);
      fd.append('priority', priority);
      if (file) fd.append('attachment', file);
      const res = await fetch(`${API_BASE_URL}/api/support/tickets`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submit failed');
      }
      const data = await res.json();
      setSuccess(data);
    } catch (e2) {
      setError(e2.message || 'Could not submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="support-modal-overlay"
      onClick={handleClose}
      style={{ position: 'fixed', inset: 0,
               background: 'rgba(15,23,42,0.55)',
               display: 'flex', alignItems: 'center',
               justifyContent: 'center', zIndex: 9500 }}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="support-modal"
        style={{ background: '#fff', borderRadius: 14,
                 width: 'min(620px, 92vw)', padding: '1.25rem 1.4rem',
                 maxHeight: '92vh', overflowY: 'auto',
                 boxShadow: '0 30px 80px rgba(15,23,42,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', marginBottom: '0.6rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>
              Contact Support
            </h3>
            <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: 2 }}>
              We aim to reply within one business day.
            </div>
          </div>
          <button type="button"
                  onClick={handleClose}
                  data-testid="btn-close-support-modal"
                  style={{ background: 'transparent', border: 'none',
                           fontSize: '1.4rem', cursor: 'pointer',
                           color: '#94a3b8' }}>×</button>
        </div>

        {success ? (
          <div data-testid="support-success"
               style={{ background: '#ecfdf5', border: '1px solid #a7f3d0',
                        color: '#065f46', padding: '0.9rem 1rem',
                        borderRadius: 8, fontSize: '0.92rem' }}>
            <strong>Thank you.</strong> Ticket <code>{success.ticket_id}</code> has
            been created at <strong>{success.priority?.toUpperCase()}</strong> priority.
            Our team will reply to your registered email shortly.
            <div style={{ marginTop: 12 }}>
              <button type="button"
                      onClick={handleClose}
                      data-testid="btn-support-done"
                      style={{ background: '#0073EA', color: '#fff',
                               border: 'none', borderRadius: 6,
                               padding: '0.45rem 1rem', fontWeight: 600,
                               cursor: 'pointer' }}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={{ display: 'block', fontSize: '0.75rem',
                            color: '#334155', fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              maxLength={140}
              data-testid="input-support-subject"
              style={{ width: '100%', padding: '0.5rem 0.65rem', marginTop: 4,
                       border: '1px solid #cbd5e1', borderRadius: 6,
                       fontSize: '0.92rem' }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                          gap: '0.75rem', marginTop: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem',
                                color: '#334155', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="input-support-category"
                  style={{ width: '100%', padding: '0.5rem 0.55rem', marginTop: 4,
                           border: '1px solid #cbd5e1', borderRadius: 6,
                           fontSize: '0.92rem', background: '#fff' }}>
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem',
                                color: '#334155', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  data-testid="input-support-priority"
                  style={{ width: '100%', padding: '0.5rem 0.55rem', marginTop: 4,
                           border: '1px solid #cbd5e1', borderRadius: 6,
                           fontSize: '0.92rem', background: '#fff' }}>
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <label style={{ display: 'block', fontSize: '0.75rem',
                            color: '#334155', fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                            marginTop: 12 }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              required
              data-testid="input-support-description"
              placeholder="Steps to reproduce, expected behaviour, links to specific cases…"
              style={{ width: '100%', padding: '0.6rem 0.7rem', marginTop: 4,
                       border: '1px solid #cbd5e1', borderRadius: 6,
                       fontSize: '0.92rem', lineHeight: 1.5,
                       fontFamily: 'inherit' }}
            />

            <label style={{ display: 'block', fontSize: '0.75rem',
                            color: '#334155', fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                            marginTop: 12 }}>
              Attachment (optional, max 10 MB)
            </label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files && e.target.files[0])}
              data-testid="input-support-attachment"
              style={{ marginTop: 4, fontSize: '0.85rem' }}
            />

            {error && (
              <div data-testid="support-error"
                   style={{ color: '#b91c1c', fontSize: '0.85rem',
                            marginTop: 10, background: '#fef2f2',
                            border: '1px solid #fecaca', padding: '0.5rem 0.7rem',
                            borderRadius: 6 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end',
                          gap: '0.5rem', marginTop: 16 }}>
              <button type="button"
                      onClick={handleClose}
                      disabled={submitting}
                      data-testid="btn-cancel-support"
                      style={{ background: '#e2e8f0', color: '#0f172a',
                               border: 'none', borderRadius: 6,
                               padding: '0.5rem 1rem', fontWeight: 600,
                               cursor: submitting ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
              <button type="submit"
                      disabled={submitting}
                      data-testid="btn-submit-support"
                      style={{ background: submitting ? '#94a3b8' : '#0073EA',
                               color: '#fff', border: 'none', borderRadius: 6,
                               padding: '0.5rem 1.1rem', fontWeight: 600,
                               cursor: submitting ? 'not-allowed' : 'pointer' }}>
                {submitting ? 'Submitting…' : 'Submit ticket'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
